import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

export interface AlertOverride {
  id: string;
  organizationId: string;
  taskId: string;
  deviceId: string | null;
  regionId: string | null;
  divisionId: string | null;
  overrideType: string;
  silenceUntil: string | null;
  silenceReason: string | null;
  overrideValue: Record<string, unknown> | null;
  /** When set, only alerts whose dedupe_key matches this pattern are
   *  suppressed (exact match or `*` glob). NULL means scope-only. */
  dedupePattern: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

interface AlertOverrideListResponse {
  overrides: AlertOverride[];
  total: number;
}

export interface CreateAlertOverrideRequest {
  deviceId?: string;
  regionId?: string;
  divisionId?: string;
  overrideType: string;
  silenceUntil?: string;
  silenceReason?: string;
  overrideValue?: Record<string, unknown>;
  dedupePattern?: string;
}

export interface BulkSilenceRequest {
  silenceUntil?: string;
  silenceReason?: string;
}

interface BulkSilenceResponse {
  overridesCreated: number;
}

const backendApi = config.backendApi;

export function useAlertOverrides(taskId: string | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<AlertOverride[]>({
    queryKey: ['alert-overrides', taskId],
    queryFn: async () => {
      if (!taskId) throw new Error('taskId required');
      const response = await axios.get<AlertOverrideListResponse>(
        `${backendApi}/api/alert-definitions/${taskId}/overrides`,
        { withCredentials: true }
      );
      return response.data.overrides;
    },
    enabled: !!taskId && contextReady,
  });
}

export function useCreateAlertOverride() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, data }: { taskId: string; data: CreateAlertOverrideRequest }) => {
      const response = await axios.post<AlertOverride>(
        `${backendApi}/api/alert-definitions/${taskId}/overrides`,
        data,
        { withCredentials: true }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['alert-overrides', variables.taskId] });
    },
  });
}

export function useDeleteAlertOverride() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, overrideId }: { taskId: string; overrideId: string }) => {
      await axios.delete(
        `${backendApi}/api/alert-definitions/${taskId}/overrides/${overrideId}`,
        { withCredentials: true }
      );
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['alert-overrides', variables.taskId] });
    },
  });
}

export function useBulkSilenceDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ deviceId, data }: { deviceId: string; data: BulkSilenceRequest }) => {
      const response = await axios.post<BulkSilenceResponse>(
        `${backendApi}/api/devices/${deviceId}/silence-alerts`,
        data,
        { withCredentials: true }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['alert-overrides'] });
    },
  });
}

export function useBulkSilenceRegion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ regionId, data }: { regionId: string; data: BulkSilenceRequest }) => {
      const response = await axios.post<BulkSilenceResponse>(
        `${backendApi}/api/regions/${regionId}/silence-alerts`,
        data,
        { withCredentials: true }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['alert-overrides'] });
    },
  });
}
